import DiffMatchPatch from "diff-match-patch"

import {
  getScriptTurnKind,
  getScriptTurnText,
  isDtmfScriptTurn,
  type ScriptTurn,
} from "@/types/ScriptTurn"

import type { EditableTestCase, PendingChanges } from "../model"

function joinTurnsForDiff(turns: ScriptTurn[] | null | undefined): string {
  if (!turns) {
    return ""
  }
  return turns
    .map((turn) => {
      const kind = getScriptTurnKind(turn)
      if (kind === "dtmf" && isDtmfScriptTurn(turn)) {
        return `[DTMF ${turn.digits}]`
      }
      if (kind === "unknown") {
        const type =
          typeof turn === "object" && turn !== null
            ? String(turn.type)
            : "unknown"
        return `[${type} turn]`
      }
      return getScriptTurnText(turn) ?? ""
    })
    .join("\n")
}

function serializeTestCase(testCase: EditableTestCase) {
  return [
    testCase.input_str,
    testCase.description,
    (testCase.expected_behaviors || []).join("\n"),
  ]
    .filter(Boolean)
    .join("\n")
}

export function getPendingDiffSummaries(
  pendingChanges: PendingChanges | null,
  testCases: EditableTestCase[],
): Record<string, { added: number; removed: number }> | null {
  if (!pendingChanges) {
    return null
  }

  interface DiffPair {
    id: string
    newText: string
    oldText: string
  }

  const pairs: DiffPair[] =
    pendingChanges.type === "ADD"
      ? pendingChanges.data.map((testCase) => ({
          id: testCase.id,
          oldText: "",
          newText: serializeTestCase(testCase),
        }))
      : pendingChanges.type === "DELETE"
        ? pendingChanges.data
            .map((id) => {
              const testCase = testCases.find(
                (candidate) => candidate.id === id,
              )
              return testCase
                ? { id, oldText: serializeTestCase(testCase), newText: "" }
                : null
            })
            .filter((pair) => pair !== null)
        : pendingChanges.data
            .map((edit) => {
              const testCase = testCases.find(
                (candidate) => candidate.id === edit.id,
              )
              if (!testCase) {
                return null
              }

              const oldParts: string[] = []
              const newParts: string[] = []

              for (const field of [
                "input_str",
                "description",
                "input_type",
              ] as const) {
                const value = edit[field]
                if (value != null) {
                  oldParts.push((testCase[field] as string) || "")
                  newParts.push(value)
                }
              }

              if (edit.expected_behaviors != null) {
                oldParts.push((testCase.expected_behaviors || []).join("\n"))
                newParts.push(edit.expected_behaviors.join("\n"))
              }

              if (edit.expected_output_json !== undefined) {
                oldParts.push(
                  JSON.stringify(testCase.expected_output_json || {}, null, 2),
                )
                newParts.push(
                  JSON.stringify(edit.expected_output_json || {}, null, 2),
                )
              }

              if (edit.script_turns != null) {
                oldParts.push(joinTurnsForDiff(testCase.script_turns))
                newParts.push(joinTurnsForDiff(edit.script_turns))
              }

              return {
                id: edit.id,
                oldText: oldParts.join("\n"),
                newText: newParts.join("\n"),
              }
            })
            .filter((pair) => pair !== null)

  const diffEngine = new DiffMatchPatch()
  const result: Record<string, { added: number; removed: number }> = {}

  for (const { id, oldText, newText } of pairs) {
    const diffs = diffEngine.diff_main(oldText, newText)
    diffEngine.diff_cleanupSemantic(diffs)

    const summary = diffs.reduce(
      (counts, [operation, text]) => {
        const words = text.trim().split(/\s+/).filter(Boolean).length
        if (operation === 1) {
          counts.added += words
        }
        if (operation === -1) {
          counts.removed += words
        }
        return counts
      },
      { added: 0, removed: 0 },
    )

    if (summary.added > 0 || summary.removed > 0) {
      result[id] = summary
    }
  }

  return result
}

export function getPendingTestCaseIds(
  pendingChanges: PendingChanges | null,
): Set<string> {
  if (!pendingChanges) {
    return new Set<string>()
  }

  return pendingChanges.type === "DELETE"
    ? new Set(pendingChanges.data)
    : new Set(pendingChanges.data.map((entry) => entry.id))
}
